/**
 * @file        src/components/RefundPipeline.jsx
 * @description Refund workflow card (CR E1) shared by the flight and hotel
 *              Details drawers. Shows the refund amounts, the current status
 *              as a stepper (Requested → Approved → Processed → Credited, or
 *              Rejected), the timestamped history trail, and the admin action
 *              buttons for the legal next transitions (with an optional note).
 *              Renders nothing when the booking has no refund in progress.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-10-10 Claude — "Our amendment markup" row (migration 078), shown only
 *                          when the booking carries one.
 *   - 2026-08-04 Claude — Initial (unified refund pipeline UI).
 *   - 2026-09-15 Claude — API errors read via apiErrorMessage(): the error handler returns {error:{code,message}}, and putting that object into state/toast threw "Objects are not valid as a React child", blanking the whole app.
 *   - 2026-09-17 Claude — The card now answers the refund desk's actual question:
 *     how much still has to be moved, and why the refund is smaller than the
 *     fare. It showed only the two instrument figures, so a fully-wallet refund
 *     read as "Gateway refund ₹0.00" with nothing saying the customer had
 *     already been paid back in full. Adds Refund due (total), Penalty retained
 *     (rendered '—' when NULL — never quoted is not the same as the supplier
 *     keeping nothing) and To refund via gateway, which drops to ₹0 on credited.
 *     Also explains the legitimate state the client queried: a refund credited
 *     while the booking is still awaiting the supplier's cancellation verdict.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Chip from './Chip';
import { post, apiErrorMessage } from '../api/client';
import { toast } from '../lib/toast';

const STEPS = ['requested', 'approved', 'processed', 'credited'];
const LABEL = {
  requested: 'Requested', approved: 'Approved', processed: 'Processed',
  credited: 'Credited', rejected: 'Rejected',
};
// Legal next moves — mirrors REFUND_FLOW on the server (which enforces it).
const NEXT = {
  requested: ['approved', 'rejected'],
  approved: ['processed', 'rejected'],
  processed: ['credited'],
};
const BTN_LABEL = {
  approved: 'Approve', processed: 'Mark processed',
  credited: 'Mark credited', rejected: 'Reject',
};

const inr2 = (n) =>
  `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function dt(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: true,
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export default function RefundPipeline({ kind, bookingId, refund, detailsQueryKey, bookingStatus }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const status = refund?.status || null;

  const mut = useMutation({
    mutationFn: (to) => post(`/${kind}/bookings/${bookingId}/refund/status`, { status: to, note: note.trim() }),
    onSuccess: (_res, to) => {
      toast.ok('Refund updated', `Refund marked ${LABEL[to] || to}.`);
      setNote('');
      qc.invalidateQueries({ queryKey: detailsQueryKey });
      qc.invalidateQueries({ queryKey: ['booking-logs', kind, bookingId] });
    },
    onError: (err) => {
      toast.err('Could not update refund', apiErrorMessage(err, 'Try again.'));
    },
  });

  if (!status) return null;

  const doneIdx = STEPS.indexOf(status);
  const nexts = NEXT[status] || [];
  // Still with the supplier: our side of the money is done but the seat/room has
  // not been released yet, so booking_status is legitimately not 'Cancelled'.
  const awaitingSupplier = /requested/i.test(String(bookingStatus || ''));

  return (
    <>
      <h4 className="det-h">Refund</h4>
      <div className="det-grid">
        {/* The total agreed with the supplier, of which the two rows below are
            the legs. Older bookings never recorded it, hence the '—'. */}
        <div><span className="muted">Refund due (total)</span><div><b>{refund.gross != null ? inr2(refund.gross) : '—'}</b></div></div>
        {/* NULL means the charge was never quoted — NOT that the supplier kept
            nothing. Rendering it as ₹0.00 would assert a fact we do not have. */}
        <div>
          <span className="muted">Penalty retained</span>
          <div>{refund.penalty != null ? inr2(refund.penalty) : <span className="muted">— not quoted</span>}</div>
        </div>
        {/* Our own amendment markup (migration 078). Console-only: the customer
            sees it only inside the cancellation charge. Cumulative across this
            booking's amendments (a reschedule or add-on markup counts too). */}
        {Number(refund.amendment_markup) > 0 && (
          <div>
            <span className="muted">Our amendment markup</span>
            <div>{inr2(refund.amendment_markup)} <span className="muted">(not shown to customer)</span></div>
          </div>
        )}
        <div><span className="muted">Wallet refund</span><div>{inr2(refund.wallet_amount)} <span className="muted">(instant)</span></div></div>
        <div><span className="muted">Gateway refund</span><div><b>{refund.amount != null ? inr2(refund.amount) : '—'}</b></div></div>
        {/* The desk's actual queue: money a human still has to move. Zero once
            the refund is credited, whatever the gateway leg was. */}
        <div>
          <span className="muted">To refund via gateway</span>
          <div><b>{inr2(refund.gateway_pending != null ? refund.gateway_pending : (status === 'credited' ? 0 : refund.amount))}</b></div>
        </div>
        <div>
          <span className="muted">Status</span>
          <div><Chip tone={status === 'credited' ? 'ok' : status === 'rejected' ? 'rej' : 'pend'}>{LABEL[status] || status}</Chip></div>
        </div>
      </div>

      {/* A credited refund on a booking the supplier has not finished cancelling
          is a REAL, correct state, and it reads like a contradiction on screen —
          it is exactly what was raised as a defect. Say so, rather than leaving
          the two chips to disagree silently. */}
      {status === 'credited' && awaitingSupplier ? (
        <p className="muted" style={{ margin: '8px 0 0', fontSize: 12 }}>
          The refund is complete. The booking stays on “{bookingStatus}” until the
          {kind === 'hotel' ? ' hotel' : ' airline'} confirms the cancellation — it moves to
          “Cancelled” automatically then.
        </p>
      ) : null}

      {/* Stepper — rejected replaces the rail with its terminal chip above. */}
      {status !== 'rejected' ? (
        <div className="rp-steps">
          {STEPS.map((s, i) => (
            <span key={s} className={`rp-step${i <= doneIdx ? ' done' : ''}`}>
              <span className="rp-dot" />{LABEL[s]}
            </span>
          ))}
        </div>
      ) : null}

      {(refund.history || []).length ? (
        <ul className="tl-list">
          {refund.history.map((h, i) => (
            <li className="tl-item" key={i}>
              <span className={`tl-dot ${h.status === 'rejected' ? 'danger' : h.status === 'credited' ? '' : 'warn'}`} />
              <div className="tl-head">
                <b>{LABEL[h.status] || h.status}</b>
                <span className="tl-when">{dt(h.created_at)}</span>
                <span className="tl-when">
                  · {h.actor_type === 'admin' ? `Admin${h.actor_id ? ` #${h.actor_id}` : ''}`
                    : h.actor_type === 'customer' ? 'Customer' : 'System'}
                </span>
              </div>
              {h.note ? <div className="tl-detail">{h.note}</div> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {nexts.length ? (
        <div className="rp-actions">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (kept on the refund trail — required for Reject)"
            maxLength={500}
          />
          {nexts.map((to) => (
            <button
              key={to}
              className={`btn ${to === 'rejected' ? 'danger' : to === 'credited' ? 'success' : 'primary'} sm`}
              disabled={mut.isPending || (to === 'rejected' && !note.trim())}
              onClick={() => mut.mutate(to)}
            >
              {BTN_LABEL[to]}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}
